import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BarChart3, CalendarDays, Loader2, RefreshCw, Search, User, Users, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SearchableAgentSelect from "@/components/common/SearchableAgentSelect";
import { cn } from "@/lib/utils";
import {
  PanelTableCard,
  PanelTableHeader,
  PanelTableFrame,
  PanelTableHeadRow,
  PanelTableHead,
  PanelTableBody,
  PanelTableRow,
  PanelTableCell,
  PanelTableEmpty,
  PanelTablePagination,
  PanelTableFooter,
  usePanelTablePagination,
} from "@/components/ui/panel-table";

const formatMoney = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString("tr-TR", { maximumFractionDigits: 0 });
};

const formatTrDate = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

const SummaryTotalsFooter = ({ rows, isDark, textClass, labelClass }) => {
  const t = rows.reduce(
    (acc, r) => ({
      islem: acc.islem + (r.islem_total ?? 0),
      islemT: acc.islemT + (r.islem_tutari_total ?? 0),
      muayene: acc.muayene + (r.muayene_total ?? 0),
      muayeneT: acc.muayeneT + (r.muayene_tutari_total ?? 0),
      iptal: acc.iptal + (r.iptal_total ?? 0),
      iptalT: acc.iptalT + (r.iptal_tutari_total ?? 0),
      ciro: acc.ciro + (r.ciro_total ?? 0),
    }),
    { islem: 0, islemT: 0, muayene: 0, muayeneT: 0, iptal: 0, iptalT: 0, ciro: 0 },
  );
  return (
    <tr className={isDark ? "bg-slate-900/70 border-t border-slate-700 font-semibold" : "bg-emerald-50/90 border-t border-emerald-200 font-semibold"}>
      <td className={`px-3 py-2 ${textClass}`} colSpan={2}>Toplam</td>
      <td className={`px-3 py-2 text-right tabular-nums ${textClass}`}>{t.islem}</td>
      <td className={`px-3 py-2 text-right tabular-nums ${textClass}`}>{formatMoney(t.islemT)}</td>
      <td className={`px-3 py-2 text-right tabular-nums ${textClass}`}>{t.muayene}</td>
      <td className={`px-3 py-2 text-right tabular-nums ${textClass}`}>{formatMoney(t.muayeneT)}</td>
      <td className={`px-3 py-2 text-right tabular-nums ${textClass}`}>{t.iptal}</td>
      <td className={`px-3 py-2 text-right tabular-nums ${textClass}`}>{formatMoney(t.iptalT)}</td>
      <td className={`px-3 py-2 text-right tabular-nums ${textClass}`}>{formatMoney(t.ciro)}</td>
    </tr>
  );
};

const DAILY_PAGE_SIZE = 25;

const CiroExcelRecordsPage = ({
  apiBase,
  isDark,
  textClass,
  labelClass,
  inputClass,
  isSuperAdmin,
  userCompanyId,
  companies = [],
}) => {
  const [companyId, setCompanyId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [leaderId, setLeaderId] = useState("");
  const [agentFilterId, setAgentFilterId] = useState(null);
  const [listPage, setListPage] = useState(1);
  const [leaders, setLeaders] = useState([]);
  const [companyAgents, setCompanyAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("daily_detail");

  const spnDefault = useMemo(
    () => companies.find((c) => String(c.kod || "").toLowerCase() === "spn" && c.status === 1)?.id,
    [companies],
  );

  const effectiveCompanyId = useMemo(() => {
    if (isSuperAdmin) {
      const n = Number(companyId);
      return companyId && Number.isFinite(n) && n > 0 ? n : null;
    }
    const n = Number(userCompanyId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [isSuperAdmin, companyId, userCompanyId]);

  useEffect(() => {
    if (!isSuperAdmin || companyId || !spnDefault) return;
    setCompanyId(String(spnDefault));
  }, [isSuperAdmin, companyId, spnDefault]);

  useEffect(() => {
    setAgentFilterId(null);
    setLeaderId("");
    setListPage(1);
    setData(null);
  }, [effectiveCompanyId]);

  const selectedAgent = useMemo(() => {
    if (!agentFilterId) return null;
    return companyAgents.find((a) => Number(a.id) === Number(agentFilterId)) ?? null;
  }, [agentFilterId, companyAgents]);

  useEffect(() => {
    if (!effectiveCompanyId) {
      setLeaders([]);
      setCompanyAgents([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [metaRes, agentsRes] = await Promise.all([
          axios.get(`${apiBase}/team-leader-performance`, {
            params: { action: "meta", company_id: effectiveCompanyId },
          }),
          axios.get(`${apiBase}/agents`, { timeout: 90000 }),
        ]);
        if (!cancelled) setLeaders(metaRes.data?.leaders || []);
        const raw = Array.isArray(agentsRes.data) ? agentsRes.data : (agentsRes.data?.data || []);
        if (!cancelled) {
          setCompanyAgents(raw.filter((a) => Number(a.company_id) === Number(effectiveCompanyId)));
        }
      } catch {
        if (!cancelled) {
          setLeaders([]);
          setCompanyAgents([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase, effectiveCompanyId]);

  const load = useCallback(async (options = {}) => {
    if (!effectiveCompanyId) {
      toast.warning("Firma seçin");
      return;
    }
    const resolvedAgent = options.agentId !== undefined ? options.agentId : agentFilterId;
    const agentIdParam = resolvedAgent != null && Number.isFinite(Number(resolvedAgent)) && Number(resolvedAgent) > 0
      ? Number(resolvedAgent)
      : undefined;
    const page = Math.max(1, options.page !== undefined ? Number(options.page) : 1);
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/tl-ciro-records`, {
        params: {
          company_id: effectiveCompanyId,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          search: search.trim() || undefined,
          leader_user_id: leaderId || undefined,
          agent_id: agentIdParam,
          limit: DAILY_PAGE_SIZE,
          page,
        },
        timeout: 120000,
      });
      setData(res.data);
      setListPage(Number(res.data?.page) > 0 ? Number(res.data.page) : page);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ciro kayıtları yüklenemedi");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiBase, effectiveCompanyId, startDate, endDate, search, leaderId, agentFilterId]);

  const runListQuery = useCallback(
    (overrides = {}) => {
      setListPage(1);
      return load({ ...overrides, page: 1 });
    },
    [load],
  );

  useEffect(() => {
    if (effectiveCompanyId) runListQuery();
  }, [effectiveCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps -- yalnızca firma değişince ilk yükleme

  const items = Array.isArray(data?.items) ? data.items : [];
  const assistantSummary = Array.isArray(data?.assistant_summary) ? data.assistant_summary : [];
  const dailySummary = Array.isArray(data?.daily_summary) ? data.daily_summary : [];

  const dailyTotal = Number(data?.total) || 0;
  const dailyLimit = Number(data?.limit) > 0 ? Number(data.limit) : DAILY_PAGE_SIZE;
  const dailyTotalPages = Math.max(1, Math.ceil(dailyTotal / dailyLimit) || 1);
  const dailyRangeStart = dailyTotal === 0 ? 0 : (listPage - 1) * dailyLimit + 1;
  const dailyRangeEnd = dailyTotal === 0 ? 0 : Math.min(listPage * dailyLimit, dailyTotal);
  const pageRows = items;

  const assistantPagination = usePanelTablePagination(assistantSummary, {
    pageSize: 25,
    resetDeps: [data?.total, leaderId, agentFilterId, startDate, endDate, search],
  });
  const dailySummaryPagination = usePanelTablePagination(dailySummary, {
    pageSize: 25,
    resetDeps: [data?.total, leaderId, agentFilterId, startDate, endDate, search],
  });

  const onDailyPageChange = useCallback(
    (nextPage) => {
      const p = Math.max(1, Math.min(nextPage, dailyTotalPages));
      setListPage(p);
      load({ page: p });
    },
    [load, dailyTotalPages],
  );

  const summaryFiltered = data?.summary_filtered;
  const agentFilterActive = Boolean(agentFilterId);

  const summaryBlockClass = isDark
    ? "border-slate-700/80 bg-slate-900/50"
    : "border-gray-200 bg-gray-50/90";

  const tabListClass = cn(
    "w-full justify-start h-auto flex-wrap gap-1 p-1 mb-0",
    isDark ? "bg-slate-800/80" : "bg-gray-100",
  );

  const tabTriggerClass = cn(
    "text-xs sm:text-sm gap-1.5 data-[state=active]:shadow-sm",
    isDark && "data-[state=active]:bg-slate-900 data-[state=active]:text-emerald-200",
  );

  const reportTableWrap = cn(
    "rounded-xl border overflow-hidden overflow-x-auto",
    isDark ? "border-slate-700 bg-slate-900/30" : "border-gray-200 bg-white",
  );

  const reportHead = isDark ? "bg-slate-800/90 text-slate-300" : "bg-emerald-800 text-white";

  const metricHead = (
    <>
      <th className={`text-right px-2 py-2.5 font-semibold whitespace-nowrap ${reportHead}`}>İşlem (Adet)</th>
      <th className={`text-right px-2 py-2.5 font-semibold whitespace-nowrap ${reportHead}`}>İşlem Tutarı</th>
      <th className={`text-right px-2 py-2.5 font-semibold whitespace-nowrap ${reportHead}`}>Muayene (Adet)</th>
      <th className={`text-right px-2 py-2.5 font-semibold whitespace-nowrap ${reportHead}`}>Muayene Tutarı</th>
      <th className={`text-right px-2 py-2.5 font-semibold whitespace-nowrap ${reportHead}`}>İptal (Adet)</th>
      <th className={`text-right px-2 py-2.5 font-semibold whitespace-nowrap ${reportHead}`}>İptal Tutarı</th>
      <th className={`text-right px-2 py-2.5 font-semibold whitespace-nowrap ${reportHead}`}>Toplam Ciro</th>
    </>
  );

  const metricCells = (row) => (
    <>
      <td className={`px-2 py-1.5 text-right tabular-nums ${textClass}`}>{row.islem_total}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums ${textClass}`}>{formatMoney(row.islem_tutari_total)}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums ${textClass}`}>{row.muayene_total}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums ${textClass}`}>{formatMoney(row.muayene_tutari_total)}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums ${textClass}`}>{row.iptal_total}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums ${textClass}`}>{formatMoney(row.iptal_tutari_total)}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${textClass}`}>{formatMoney(row.ciro_total)}</td>
    </>
  );

  return (
    <PanelTableCard isDark={isDark}>
      <PanelTableHeader
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
        accent="teal"
        icon={Wallet}
        title="Ciro Kayıtları (Excel)"
        description="Günlük asistan detayı, asistan genel özeti ve günlük genel özet — veritabanındaki tüm ciro satırlarından üretilir."
        stackToolbar
        toolbar={(
          <div className={`flex flex-wrap items-end gap-2 rounded-xl border px-3 py-2.5 w-full ${isDark ? "border-slate-700/80 bg-slate-900/40" : "border-gray-200 bg-white/80"}`}>
            {isSuperAdmin && (
              <div className="w-full sm:w-44">
                <label className={`text-[10px] ${labelClass}`}>Firma</label>
                <Select value={companyId || "none"} onValueChange={(v) => setCompanyId(v === "none" ? "" : v)}>
                  <SelectTrigger className={`h-9 mt-0.5 text-sm ${inputClass}`}>
                    <SelectValue placeholder="Firma" />
                  </SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="none">Seçin</SelectItem>
                    {companies.filter((c) => c.status === 1).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="w-full sm:w-44">
              <label className={`text-[10px] ${labelClass}`}>Takım lideri</label>
              <Select value={leaderId || "all"} onValueChange={(v) => setLeaderId(v === "all" ? "" : v)}>
                <SelectTrigger className={`h-9 mt-0.5 text-sm ${inputClass}`}>
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="all">Tüm liderler</SelectItem>
                  {leaders.map((l) => (
                    <SelectItem key={l.id} value={String(l.id)}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-52">
              <label className={`text-[10px] ${labelClass}`}>Agent</label>
              <SearchableAgentSelect
                agents={companyAgents}
                value={selectedAgent}
                onChange={(agent) => setAgentFilterId(agent?.id != null ? Number(agent.id) : null)}
                isDark={isDark}
                inputClass={inputClass}
                labelClass={labelClass}
                placeholder="Tüm agentlar"
                nameOnly
                className="mt-0.5"
                size="sm"
              />
            </div>
            <div>
              <label className={`text-[10px] ${labelClass}`}>Tedavi başlangıç</label>
              <Input type="date" className={`h-9 mt-0.5 w-36 ${inputClass}`} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className={`text-[10px] ${labelClass}`}>Tedavi bitiş</label>
              <Input type="date" className={`h-9 mt-0.5 w-36 ${inputClass}`} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className={`text-[10px] ${labelClass}`}>Ara</label>
              <div className="relative mt-0.5">
                <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${labelClass}`} />
                <Input className={`h-9 pl-8 ${inputClass}`} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Asistan / lider…" />
              </div>
            </div>
            <Button type="button" size="sm" className="h-9" disabled={loading} onClick={() => runListQuery()}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Listele
            </Button>
          </div>
        )}
      />

      <div className={`px-4 pt-3 ${isDark ? "border-b border-slate-800" : "border-b border-gray-100"}`}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={tabListClass}>
            <TabsTrigger value="daily_detail" className={tabTriggerClass}>
              <Users className="w-3.5 h-3.5" />
              Günlük Asistan Detay
            </TabsTrigger>
            <TabsTrigger value="assistant_summary" className={tabTriggerClass}>
              <BarChart3 className="w-3.5 h-3.5" />
              Asistan Genel Özeti
            </TabsTrigger>
            <TabsTrigger value="daily_summary" className={tabTriggerClass}>
              <CalendarDays className="w-3.5 h-3.5" />
              Günlük Genel Özet
            </TabsTrigger>
          </TabsList>

          <TabsContent value="daily_detail" className="mt-0 pt-3 pb-0">
            <PanelTableFrame
              isDark={isDark}
              footer={dailyTotal > 0 ? (
                <PanelTablePagination
                  isDark={isDark}
                  page={listPage}
                  pageSize={dailyLimit}
                  total={dailyTotal}
                  totalPages={dailyTotalPages}
                  rangeStart={dailyRangeStart}
                  rangeEnd={dailyRangeEnd}
                  onPageChange={onDailyPageChange}
                />
              ) : null}
            >
              <table className="w-full text-sm">
                <PanelTableHeadRow isDark={isDark}>
                  <PanelTableHead isDark={isDark}>Tedavi tarihi</PanelTableHead>
                  <PanelTableHead isDark={isDark}>Asistan</PanelTableHead>
                  <PanelTableHead isDark={isDark}>Lider</PanelTableHead>
                  <PanelTableHead isDark={isDark} className="text-right">İşlem</PanelTableHead>
                  <PanelTableHead isDark={isDark} className="text-right">İşlem ₺</PanelTableHead>
                  <PanelTableHead isDark={isDark} className="text-right">Muayene</PanelTableHead>
                  <PanelTableHead isDark={isDark} className="text-right">Muayene ₺</PanelTableHead>
                  <PanelTableHead isDark={isDark} className="text-right">İptal</PanelTableHead>
                  <PanelTableHead isDark={isDark} className="text-right">Toplam ciro</PanelTableHead>
                </PanelTableHeadRow>
                <PanelTableBody>
                  {loading && (
                    <PanelTableRow isDark={isDark}>
                      <PanelTableCell colSpan={9} className="text-center py-8">
                        <Loader2 className={`w-6 h-6 animate-spin mx-auto ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                      </PanelTableCell>
                    </PanelTableRow>
                  )}
                  {!loading && pageRows.length === 0 && (
                    <PanelTableEmpty colSpan={9} isDark={isDark} title="Kayıt yok" description="TL Performans → Ciro Excel ile «Günlük Asistan Detay» yükleyin." />
                  )}
                  {!loading && pageRows.map((row) => (
                    <PanelTableRow key={row.id} isDark={isDark}>
                      <PanelTableCell className="tabular-nums whitespace-nowrap">{formatTrDate(row.tedavi_tarihi)}</PanelTableCell>
                      <PanelTableCell>
                        <div className={textClass}>{row.agent_name || row.assistant_name}</div>
                      </PanelTableCell>
                      <PanelTableCell className="text-xs">{row.leader_name || "—"}</PanelTableCell>
                      <PanelTableCell className="text-right tabular-nums">{row.islem_adeti}</PanelTableCell>
                      <PanelTableCell className="text-right tabular-nums">{formatMoney(row.islem_tutari)}</PanelTableCell>
                      <PanelTableCell className="text-right tabular-nums">{row.muayene_adeti}</PanelTableCell>
                      <PanelTableCell className="text-right tabular-nums">{formatMoney(row.muayene_tutari)}</PanelTableCell>
                      <PanelTableCell className="text-right tabular-nums">{row.iptal_adeti}</PanelTableCell>
                      <PanelTableCell className="text-right tabular-nums font-medium">{formatMoney(row.toplam_ciro)}</PanelTableCell>
                    </PanelTableRow>
                  ))}
                </PanelTableBody>
              </table>
            </PanelTableFrame>
          </TabsContent>

          <TabsContent value="assistant_summary" className="mt-0 pt-3 pb-4">
            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className={`w-8 h-8 animate-spin ${isDark ? "text-emerald-400" : "text-emerald-600"}`} /></div>
            ) : assistantSummary.length === 0 ? (
              <p className={`text-sm py-8 text-center ${labelClass}`}>Seçili filtrelerde asistan özeti yok.</p>
            ) : (
              <div className={reportTableWrap}>
                <table className="w-full text-xs min-w-[720px]">
                  <thead>
                    <tr>
                      <th className={`text-left px-3 py-2.5 font-semibold sticky left-0 ${reportHead}`}>Asistan Adı</th>
                      <th className={`text-right px-2 py-2.5 font-semibold ${reportHead}`}>Gün</th>
                      {metricHead}
                    </tr>
                  </thead>
                  <tbody>
                    {(assistantPagination.paginatedItems ?? []).map((row) => {
                      const active = row.agent_id && Number(row.agent_id) === Number(agentFilterId);
                      return (
                        <tr
                          key={`${row.agent_id ?? "n"}-${row.agent_name}`}
                          className={cn(
                            "border-t",
                            isDark ? "border-slate-800" : "border-gray-100",
                            row.agent_id && "cursor-pointer hover:bg-emerald-500/10",
                            active && (isDark ? "bg-emerald-950/50" : "bg-emerald-50"),
                          )}
                          onClick={() => {
                            if (!row.agent_id) return;
                            const aid = Number(row.agent_id);
                            setAgentFilterId(aid);
                            setActiveTab("daily_detail");
                            runListQuery({ agentId: aid });
                          }}
                        >
                          <td className={cn("px-3 py-1.5 font-medium sticky left-0", textClass, isDark ? "bg-slate-900/95" : "bg-white")}>{row.agent_name}</td>
                          <td className={`px-2 py-1.5 text-right tabular-nums ${labelClass}`}>{row.day_count}</td>
                          {metricCells(row)}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <SummaryTotalsFooter rows={assistantSummary} isDark={isDark} textClass={textClass} labelClass={labelClass} />
                  </tfoot>
                </table>
                {assistantSummary.length > assistantPagination.pageSize ? (
                  <div className={`border-t px-2 py-1 ${isDark ? "border-slate-700" : "border-gray-200"}`}>
                    <PanelTablePagination
                      isDark={isDark}
                      page={assistantPagination.page}
                      pageSize={assistantPagination.pageSize}
                      total={assistantPagination.total}
                      totalPages={assistantPagination.totalPages}
                      rangeStart={assistantPagination.rangeStart}
                      rangeEnd={assistantPagination.rangeEnd}
                      onPageChange={assistantPagination.setPage}
                    />
                  </div>
                ) : null}
              </div>
            )}
          </TabsContent>

          <TabsContent value="daily_summary" className="mt-0 pt-3 pb-4">
            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className={`w-8 h-8 animate-spin ${isDark ? "text-emerald-400" : "text-emerald-600"}`} /></div>
            ) : dailySummary.length === 0 ? (
              <p className={`text-sm py-8 text-center ${labelClass}`}>Seçili filtrelerde günlük özet yok.</p>
            ) : (
              <div className={reportTableWrap}>
                <table className="w-full text-xs min-w-[720px]">
                  <thead>
                    <tr>
                      <th className={`text-left px-3 py-2.5 font-semibold ${reportHead}`}>Tedavi Tarihi</th>
                      <th className={`text-right px-2 py-2.5 font-semibold ${reportHead}`}>Asistan</th>
                      {metricHead}
                    </tr>
                  </thead>
                  <tbody>
                    {(dailySummaryPagination.paginatedItems ?? []).map((row) => (
                      <tr key={row.report_date} className={cn("border-t", isDark ? "border-slate-800" : "border-gray-100")}>
                        <td className={`px-3 py-1.5 tabular-nums font-medium ${textClass}`}>{formatTrDate(row.report_date)}</td>
                        <td className={`px-2 py-1.5 text-right tabular-nums ${labelClass}`}>{row.agent_count}</td>
                        {metricCells(row)}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <SummaryTotalsFooter rows={dailySummary} isDark={isDark} textClass={textClass} labelClass={labelClass} />
                  </tfoot>
                </table>
                {dailySummary.length > dailySummaryPagination.pageSize ? (
                  <div className={`border-t px-2 py-1 ${isDark ? "border-slate-700" : "border-gray-200"}`}>
                    <PanelTablePagination
                      isDark={isDark}
                      page={dailySummaryPagination.page}
                      pageSize={dailySummaryPagination.pageSize}
                      total={dailySummaryPagination.total}
                      totalPages={dailySummaryPagination.totalPages}
                      rangeStart={dailySummaryPagination.rangeStart}
                      rangeEnd={dailySummaryPagination.rangeEnd}
                      onPageChange={dailySummaryPagination.setPage}
                    />
                  </div>
                ) : null}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {data && !loading && summaryFiltered && (
        <div className={`border-t ${isDark ? "border-slate-800" : "border-gray-100"}`}>
          <PanelTableFooter isDark={isDark}>
            <div className={`rounded-lg border px-3 py-2.5 w-full ${summaryBlockClass}`}>
              <p className={`text-[10px] uppercase tracking-wide font-semibold mb-1 flex items-center gap-1 ${labelClass}`}>
                <User className="w-3 h-3" />
                Seçili filtreler — toplam
              </p>
              <p className={`text-sm ${textClass}`}>
                <strong>{summaryFiltered.record_count ?? 0}</strong> günlük satır
                {" · "}
                işlem <strong>{summaryFiltered.islem_total ?? 0}</strong>
                {" · "}
                muayene <strong>{summaryFiltered.muayene_total ?? 0}</strong>
                {" · "}
                ciro <strong>{formatMoney(summaryFiltered.ciro_total)}</strong>
              </p>
              {agentFilterActive && (
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-[11px] mt-0.5"
                  onClick={() => {
                    setAgentFilterId(null);
                    runListQuery({ agentId: null });
                  }}
                >
                  Agent filtresini kaldır
                </Button>
              )}
            </div>
          </PanelTableFooter>
        </div>
      )}
    </PanelTableCard>
  );
};

export default CiroExcelRecordsPage;
